import { useCallback, useRef } from 'react';
import { MenuItemType } from '@/types';
import { useUser } from '@/queries/useUser';
import { useAuthStore } from '@/stores';
import { useStanRouter } from '@/hooks/useStanRouter';
import { configSettings } from '@/lib/config';
import { usePinnedNavigationMutation } from '../queries/usePinnedNavigation';
import { buildCommunityUrl, calculateNextPinnedState, isMenuItemPinned } from '../logic/moreOptions.logic';
import { UserWithCommunity } from '../types';

export const useMoreOptionsActions = () => {
    const { user } = useUser();
    const token = useAuthStore(state => state.token);
    const { push } = useStanRouter();
    const { mutateAsync: updatePinnedNavigation } = usePinnedNavigationMutation();

    // Track in-flight toggle requests to prevent double-clicks
    const inFlightRef = useRef<Set<string>>(new Set());

    const isItemPinned = useCallback(
        (item: MenuItemType) => {
            const pinnedIds = user?.data?.pinned_navigation?.map(p => p.id) ?? [];
            return isMenuItemPinned(item, pinnedIds);
        },
        [user?.data?.pinned_navigation]
    );

    const isToggleDisabled = useCallback((item: MenuItemType) => inFlightRef.current.has(item.id), []);

    const handleItemClick = useCallback(
        (item: MenuItemType) => {
            if (item.redirect) {
                window.open(item.route, '_blank');
            } else {
                push(item.route);
            }
        },
        [push]
    );

    const handleToggle = useCallback(
        async (item: MenuItemType) => {
            if (!user || inFlightRef.current.has(item.id)) return;

            const isPinned = isItemPinned(item);
            const currentPinned = user.data?.pinned_navigation ?? [];
            const nextPinned = calculateNextPinnedState(currentPinned, item.id, isPinned);

            inFlightRef.current.add(item.id);
            try {
                await updatePinnedNavigation(nextPinned);
            } finally {
                inFlightRef.current.delete(item.id);
            }
        },
        [user, isItemPinned, updatePinnedNavigation]
    );

    const handleCommunityClick = useCallback(() => {
        const userWithCommunity = user as UserWithCommunity;
        const community = userWithCommunity?.community || userWithCommunity?.communities?.[0];

        if (community && token && configSettings.communityDomain) {
            const hasMultipleCommunities = (userWithCommunity?.communities?.length ?? 0) > 1;
            const communityUrl = buildCommunityUrl(
                configSettings.communityDomain,
                community.slug,
                hasMultipleCommunities,
                token
            );
            window.open(communityUrl, '_blank');
        }
    }, [user, token]);

    return {
        handleItemClick,
        handleToggle,
        handleCommunityClick,
        isItemPinned,
        isToggleDisabled,
    };
};
